import { useEffect, useRef, useState } from "react";
import { faceForLine, type useFace } from "@softmax/avatar/expressions";
import { RemarkScheduler } from "./remark-scheduler";
import { type Remark } from "./page-remarks";
import type { CogentLive, PageContext } from "../chat/chat-view";
import type { useWidgetNavigation } from "../use-widget-navigation";
type Navigation = ReturnType<typeof useWidgetNavigation>;
export function useWidgetNotices({
  live,
  pageContext,
  cogentId,
  navigation,
  playFace,
}: {
  live: CogentLive | null;
  pageContext: PageContext | null;
  cogentId: string | null;
  navigation: Navigation;
  playFace: ReturnType<typeof useFace>["play"];
}) {
  const { open, log: logRef } = navigation;
  const [remark, setRemark] = useState<Remark | null>(null);
  const scheduler = useRef<RemarkScheduler | null>(null);
  const previousPage = useRef<{
    path: string | undefined;
    facts: string;
  } | null>(null);
  useEffect(() => {
    const current = new RemarkScheduler({ onChange: setRemark });
    scheduler.current = current;
    previousPage.current = null;
    return () => current.dispose();
  }, []);
  useEffect(() => {
    const facts = JSON.stringify(pageContext);
    const previous = previousPage.current;

    if (!previous || previous.path !== pageContext?.path)
      scheduler.current!.visit(pageContext);
    else if (previous.facts !== facts) scheduler.current!.update(pageContext);

    previousPage.current = { path: pageContext?.path, facts };
  }, [pageContext]);
  useEffect(
    () => scheduler.current!.pause({ paused: !cogentId || open }),
    [cogentId, open],
  );
  useEffect(() => {
    if (remark) playFace(faceForLine(remark.text), 2400);
  }, [remark, playFace]);

  const heard = useRef<number | null>(null);
  useEffect(() => {
    if (!live?.loaded) return;

    const said = live.messages;

    if (heard.current !== null && said.length > heard.current) {
      const newest = said.at(-1)!;

      if (newest.from === "cogent") playFace(faceForLine(newest.text), 2400);
    }

    heard.current = said.length;
  }, [live?.loaded, live?.messages, playFace]);
  const newest = live?.messages.at(-1);
  const newestId = newest?.id;
  const pending = newest?.pending;
  const draft = live?.draft;
  useEffect(() => {
    if (!open && !newestId && !pending && !draft) return;

    const log = logRef.current;

    if (log) log.scrollTop = log.scrollHeight;
  }, [newestId, pending, draft, open, logRef]);
  return {
    remark,
    holdRemark: (hold: boolean) => scheduler.current!.hold({ on: hold }),
    dismissRemark: () => scheduler.current!.dismiss(),
  };
}
